import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { getSessionToken } from "../hooks/useSession";
import { Button } from "./ui/Button";
import { DataTableSkeleton } from "./ui/DataTableSkeleton";
import { ModalOverlay } from "./ui/Modal";
import { PageHeader } from "./ui/PageHeader";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface SmsUpline {
  id: string;
  name: string;
  smsLink: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Pagination {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export function SmsUplineManagement() {
  const [uplines, setUplines] = useState<SmsUpline[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    pageSize: 25,
    totalPages: 1,
  });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUpline, setEditingUpline] = useState<SmsUpline | null>(null);
  const [formData, setFormData] = useState({ name: "", smsLink: "", active: true });
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  const fetchUplines = useCallback(async () => {
    try {
      const token = getSessionToken();
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        pageSize: pagination.pageSize.toString(),
      });
      if (search.trim()) params.set("search", search.trim());

      const res = await fetch(`${API_BASE}/api/sms-uplines/all?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load SMS uplines");
      const json = await res.json();
      const data = Array.isArray(json.data)
        ? {
            items: json.data,
            pagination: {
              total: json.data.length,
              page: 1,
              pageSize: json.data.length || pagination.pageSize,
              totalPages: 1,
            },
          }
        : json.data;
      setUplines(data.items);
      setPagination(data.pagination);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [search, pagination.page, pagination.pageSize]);

  useEffect(() => {
    fetchUplines();
    const interval = setInterval(fetchUplines, 30000);
    return () => clearInterval(interval);
  }, [fetchUplines]);

  const handleAdd = async () => {
    if (!formData.name || !formData.smsLink) {
      setFormError("Name and SMS link are required");
      return;
    }

    setFormLoading(true);
    setFormError("");
    try {
      const token = getSessionToken();
      const res = await fetch(`${API_BASE}/api/sms-uplines`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to create upline");
      
      setShowAddModal(false);
      setFormData({ name: "", smsLink: "", active: true });
      await fetchUplines();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingUpline) return;
    if (!formData.name || !formData.smsLink) {
      setFormError("Name and SMS link are required");
      return;
    }

    setFormLoading(true);
    setFormError("");
    try {
      const token = getSessionToken();
      const res = await fetch(`${API_BASE}/api/sms-uplines/${editingUpline.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to update upline");
      
      setEditingUpline(null);
      setFormData({ name: "", smsLink: "", active: true });
      await fetchUplines();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this upline? This cannot be undone.")) return;

    try {
      const token = getSessionToken();
      const res = await fetch(`${API_BASE}/api/sms-uplines/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to delete upline");
      await fetchUplines();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to delete upline");
    }
  };

  const openAddModal = () => {
    setFormData({ name: "", smsLink: "", active: true });
    setFormError("");
    setEditingUpline(null);
    setShowAddModal(true);
  };

  const openEditModal = (upline: SmsUpline) => {
    setFormData({ name: upline.name, smsLink: upline.smsLink, active: upline.active });
    setFormError("");
    setEditingUpline(upline);
    setShowAddModal(false);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingUpline(null);
    setFormData({ name: "", smsLink: "", active: true });
    setFormError("");
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="SMS upline management"
        description="Manage Senior Market Sales mappings for Gerber and Mutual of Omaha."
        breadcrumbs={
          <div className="flex items-center gap-2 text-xs text-content-muted">
            <Link to="/dashboard/agents" className="hover:text-content-primary">Agents</Link>
            <span aria-hidden="true">/</span>
            <span>SMS uplines</span>
          </div>
        }
        actions={<Button onClick={openAddModal}>Add upline</Button>}
      />

      <div className="grid gap-3 rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-4 shadow-card md:grid-cols-[1fr_160px]">
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-content-secondary">Search name or link</span>
          <input
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPagination((current) => ({ ...current, page: 1 }));
            }}
            placeholder="Upline name or SMS link"
            className="focus-ring w-full rounded-lg border border-border-default bg-surface-overlay px-3 py-2 text-sm text-content-primary placeholder:text-content-muted"
          />
        </label>
        <label className="block min-w-36">
          <span className="mb-1.5 block text-xs font-medium text-content-secondary">Per page</span>
          <select
            value={pagination.pageSize}
            onChange={(event) => {
              setPagination((current) => ({
                ...current,
                page: 1,
                pageSize: Number(event.target.value),
              }));
            }}
            className="focus-ring w-full rounded-lg border border-border-default bg-surface-overlay px-3 py-2 text-sm text-content-primary"
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading && <DataTableSkeleton headers={["Upline", "SMS link", "Status", "Updated", "Actions"]} cellVariants={["multiline", "text", "badge", "text", "short"]} rows={6} loadingLabel="Loading SMS uplines" />}

      {error && (
        <div role="alert" className="rounded-lg border border-status-rejected-border bg-status-rejected-bg p-4 text-sm text-status-rejected-text">
          {error}
        </div>
      )}

      {!loading && !error && uplines.length === 0 && (
        <div className="rounded-[var(--card-radius)] border border-dashed border-border-default bg-surface-raised py-14 text-center">
          <p className="font-medium text-content-primary">No SMS uplines yet</p>
          <p className="mt-1 text-sm text-content-muted">Add an upline to create the first mapping.</p>
        </div>
      )}

      {!loading && uplines.length > 0 && (
        <div className="overflow-hidden rounded-[var(--card-radius)] border border-border-default bg-surface-raised shadow-card">
          {uplines.map((upline, i) => (
            <motion.div
              key={upline.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="border-b border-border-subtle px-5 py-4 last:border-b-0"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <h3 className="text-sm font-semibold text-content-primary">{upline.name}</h3>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        upline.active
                          ? "border border-status-active-border bg-status-active-bg text-status-active-text"
                          : "border border-border-default bg-surface-overlay text-content-muted"
                      }`}
                    >
                      {upline.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <a
                    href={upline.smsLink}
                    target="_blank"
                    rel="noreferrer"
                    className="break-all text-xs text-accent hover:underline"
                  >
                    {upline.smsLink}
                  </a>
                  <p className="mt-1 text-xs text-content-muted">
                    Updated {new Date(upline.updatedAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => openEditModal(upline)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => handleDelete(upline.id)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {!loading && !error && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border-default bg-surface-raised px-4 py-3 text-sm text-content-secondary">
          <span>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={pagination.page <= 1}
              onClick={() => setPagination((current) => ({ ...current, page: Math.max(1, current.page - 1) }))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() =>
                setPagination((current) => ({ ...current, page: Math.min(current.totalPages, current.page + 1) }))
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || editingUpline) && (
        <ModalOverlay
          onClose={closeModal}
          ariaLabel={editingUpline ? "Edit SMS upline" : "Add SMS upline"}
          className="[&>div]:rounded-[var(--card-radius)] [&>div]:p-6"
        >
            <h2 id="sms-upline-dialog-title" className="mb-4 text-xl font-semibold text-content-primary">
              {editingUpline ? "Edit upline" : "Add new upline"}
            </h2>

            {formError && (
              <div role="alert" className="mb-4 rounded-lg border border-status-rejected-border bg-status-rejected-bg p-3 text-sm text-status-rejected-text">
                {formError}
              </div>
            )}

            <div className="space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-content-primary">Name</span>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((d) => ({ ...d, name: e.target.value }))}
                  placeholder="John Smith"
                  className="focus-ring w-full rounded-lg border border-border-default bg-surface-overlay px-3 py-2 text-sm text-content-primary placeholder:text-content-muted"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-content-primary">SMS link</span>
                <input
                  type="url"
                  value={formData.smsLink}
                  onChange={(e) => setFormData((d) => ({ ...d, smsLink: e.target.value }))}
                  placeholder="https://seniormarketsales.com/agent/..."
                  className="focus-ring w-full rounded-lg border border-border-default bg-surface-overlay px-3 py-2 text-sm text-content-primary placeholder:text-content-muted"
                />
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData((d) => ({ ...d, active: e.target.checked }))}
                  className="focus-ring h-4 w-4 rounded border-border-default bg-surface-input text-accent accent-[var(--color-accent-strong)]"
                />
                <span className="text-sm text-content-primary">Active</span>
              </label>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <Button
                onClick={editingUpline ? handleUpdate : handleAdd}
                disabled={formLoading}
                className="flex-1"
              >
                {formLoading ? "Saving…" : editingUpline ? "Update" : "Add"}
              </Button>
              <Button
                variant="secondary"
                onClick={closeModal}
                disabled={formLoading}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
        </ModalOverlay>
      )}
    </div>
  );
}
